import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Target, AlertCircle, AlertTriangle, ChevronUp, ChevronDown, Database, Beaker } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangePicker } from "@/components/date-range-picker";
import type { DateRange } from "react-day-picker";

function fmtIso(d: Date | undefined): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function defaultRange(): DateRange {
  // Default to last 3 months — matches the Commusoft backup window with data.
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
  return { from: start, to: today };
}

// ── Types ───────────────────────────────────────────────────────────────

interface CampaignRow {
  campaign_id: string;
  campaign_name: string;
  platform: string;
  campaign_type: string;
  lead_action: string;
  product: string;
  spend: number;
  clicks: number;
  conversions: number;
  quotes_booked: number;
  sales_won: number;
  revenue_won: number;
  cpc: number | null;
  cost_per_conversion: number | null;
  conv_to_quote_pct: number | null;
  cost_per_quote: number | null;
  quote_to_sale_pct: number | null;
  cost_per_sale: number | null;
  roas: number | null;
}

interface Totals {
  spend: number; clicks: number; conversions: number; quotes_booked: number;
  sales_won: number; revenue_won: number;
  cpc: number | null; cost_per_conversion: number | null; conv_to_quote_pct: number | null;
  cost_per_quote: number | null; quote_to_sale_pct: number | null;
  cost_per_sale: number | null; roas: number | null;
}

interface Handler {
  handler_name: string;
  leads_handled: number;
  appointments_booked: number;
  lead_to_appt_pct: number | null;
  win_pct?: number | null;
  opps_won?: number;
  opps_ongoing?: number;
  opps_lost?: number;
  small_n: boolean;
}

interface HandlerCampaignCell {
  handler_name: string;
  campaign_id: string | null;
  campaign_name: string | null;
  leads_handled: number;
  appointments_booked: number;
  lead_to_appt_pct: number | null;
  win_pct?: number | null;
  small_n: boolean;
}

interface Salesperson {
  salesperson_name: string;
  quotes_issued: number;
  sales_won: number;
  revenue_won: number;
  conversion_pct: number | null;
  avg_job_value: number | null;
  small_n: boolean;
}

interface SalespersonProductCell {
  salesperson_name: string;
  product: string;
  quotes_issued: number;
  sales_won?: number;
  conversion_pct?: number | null;
  small_n?: boolean;
}

interface MIData {
  rangeStart?: string;
  rangeEnd?: string;
  month: string | null;
  availableMonths: string[];
  campaigns: CampaignRow[];
  totals: Totals;
  campaignsArePlaceholder?: boolean;
  backupFreshness?: {
    latest: string | null;
    is_stale: boolean;
  };
  handlers: Handler[];
  handlerCampaignMatrix: HandlerCampaignCell[];
  salespeople: Salesperson[];
  salespersonProductConversion: SalespersonProductCell[];
  salespersonProductQuotes: SalespersonProductCell[];
  reconciliation: {
    matches: boolean;
    leadHandlingApps: number;
    campaignQuotes: number;
    note?: string;
  };
}

const dash = "–";
function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function fmtMoney2(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return n.toLocaleString("en-GB");
}
function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return `${n.toLocaleString("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}
function fmtMul(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return `${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
}
function fmtMonth(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

function costPerSaleColor(value: number | null, min: number, max: number): string {
  if (value === null || max === min) return "";
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return t < 0.34
    ? "bg-emerald-100/70 dark:bg-emerald-950/40"
    : t < 0.67
    ? "bg-amber-100/70 dark:bg-amber-950/40"
    : "bg-rose-100/70 dark:bg-rose-950/40";
}

type SortKey = keyof CampaignRow;

export default function MarketingIntelligence() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(defaultRange());
  const [sortKey, setSortKey] = useState<SortKey>("cost_per_sale");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const from = fmtIso(dateRange?.from);
  const to = fmtIso(dateRange?.to);
  const rangeQs = from && to ? `?from=${from}&to=${to}` : "";

  const { data, isLoading, error } = useQuery<MIData>({
    queryKey: ["marketing-intelligence", from, to],
    queryFn: async () => {
      const res = await fetch(`/api/marketing-intelligence${rangeQs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading marketing intelligence…</div>;
  if (error || !data) return <div className="p-6 text-red-600">Failed to load: {error instanceof Error ? error.message : "unknown error"}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6" /> Marketing Intelligence
          </h1>
          <p className="text-sm text-muted-foreground">
            Lead handlers + salespeople come from real Commusoft opportunities. Campaign spend data is illustrative until Google Ads + Meta APIs are wired.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="ghost" size="sm" onClick={() => setDateRange(defaultRange())}>Reset</Button>
        </div>
      </div>

      {data.backupFreshness?.is_stale && (
        <Card className="border-amber-500/50">
          <CardContent className="flex items-start gap-3 p-3 text-sm">
            <Database className="h-4 w-4 text-amber-600 mt-0.5" />
            <div>
              <b>Commusoft backup is stale</b> — latest opportunity in the BigQuery snapshot is from{" "}
              <span className="font-mono">{data.backupFreshness.latest?.slice(0, 10) ?? "—"}</span>.
              Handler / salesperson tables show data only up to that date. Refresh the Commusoft backup pipeline (csbg-connector) to see current numbers.
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-blue-500/30 bg-blue-50/30 dark:bg-blue-950/10">
        <CardContent className="p-3 text-xs flex items-start gap-2 text-blue-700 dark:text-blue-300">
          <Beaker className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <b>Section 1 (Campaign league) is illustrative.</b> Sections 2 & 3 use real Commusoft opportunities for the selected date range.
            Ad spend, clicks, conversions on Section 1 will become real once Google Ads + Meta ingest are wired.
          </div>
        </CardContent>
      </Card>

      <Section1Campaigns
        data={data}
        sortKey={sortKey} setSortKey={setSortKey}
        sortDir={sortDir} setSortDir={setSortDir}
      />
      <Section2Handlers data={data} />
      <Section3Salespeople data={data} />
    </div>
  );
}

function Section1Campaigns({
  data, sortKey, setSortKey, sortDir, setSortDir,
}: {
  data: MIData;
  sortKey: SortKey;
  setSortKey: (k: SortKey) => void;
  sortDir: "asc" | "desc";
  setSortDir: (d: "asc" | "desc") => void;
}) {
  const sortedRows = useMemo(() => {
    const rows = [...data.campaigns];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const an = typeof av === "number" ? av : av === null ? Infinity : NaN;
      const bn = typeof bv === "number" ? bv : bv === null ? Infinity : NaN;
      if (typeof av === "string" || typeof bv === "string") {
        return sortDir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return sortDir === "asc" ? an - bn : bn - an;
    });
    return rows;
  }, [data.campaigns, sortKey, sortDir]);

  const costRange = useMemo(() => {
    const values = data.campaigns.map((c) => c.cost_per_sale).filter((v): v is number => v !== null);
    return { min: Math.min(...values, 0), max: Math.max(...values, 1) };
  }, [data.campaigns]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "cost_per_sale" || k === "cost_per_quote" || k === "cost_per_conversion" || k === "cpc" ? "asc" : "desc"); }
  };

  if (data.campaigns.length === 0) {
    return (
      <Card className="border-amber-500/40">
        <CardHeader>
          <CardTitle className="text-base">1. Where the money works — campaign league table</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p className="text-muted-foreground">
            <b>No live ad spend connected yet.</b> This table will populate once Google Ads and
            Meta APIs are wired into <code className="font-mono">campaign_monthly</code> in BigQuery.
          </p>
          <p className="text-xs text-muted-foreground">
            Previously this section showed placeholder figures from the original brief (e.g.
            a Facebook Solar lead-form campaign you've never run). Those have been purged so
            no fabricated spend is shown. Sections 2 &amp; 3 below use real Commusoft opportunity
            data and are unaffected.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">1. Where the money works — campaign league table</CardTitle>
        <p className="text-xs text-muted-foreground">Sort by Cost per Sale to see where each pound delivers most.</p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b">
            <tr>
              <SortableTh label="Campaign" k="campaign_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Platform" k="platform" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Type" k="campaign_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Lead action" k="lead_action" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Spend" k="spend" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Clicks" k="clicks" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="CPC" k="cpc" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Conv." k="conversions" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Cost/conv" k="cost_per_conversion" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Quotes" k="quotes_booked" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Conv→Quote%" k="conv_to_quote_pct" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Cost/quote" k="cost_per_quote" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Sales" k="sales_won" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Quote→Sale%" k="quote_to_sale_pct" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right />
              <SortableTh label="Cost/sale" k="cost_per_sale" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right hot />
              <SortableTh label="ROAS" k="roas" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} right hot />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((c) => (
              <tr key={c.campaign_id} className="border-t" data-testid={`campaign-row-${c.campaign_name}`}>
                <td className="py-1.5 pr-2 font-medium">{c.campaign_name}</td>
                <td>{c.platform}</td>
                <td>{c.campaign_type}</td>
                <td>{c.lead_action}</td>
                <td className="text-right font-mono">{fmtMoney(c.spend)}</td>
                <td className="text-right font-mono">{fmtInt(c.clicks)}</td>
                <td className="text-right font-mono">{fmtMoney2(c.cpc)}</td>
                <td className="text-right font-mono">{fmtInt(c.conversions)}</td>
                <td className="text-right font-mono">{fmtMoney2(c.cost_per_conversion)}</td>
                <td className="text-right font-mono">{fmtInt(c.quotes_booked)}</td>
                <td className="text-right font-mono">{fmtPct(c.conv_to_quote_pct)}</td>
                <td className="text-right font-mono">{fmtMoney2(c.cost_per_quote)}</td>
                <td className="text-right font-mono">{fmtInt(c.sales_won)}</td>
                <td className="text-right font-mono">{fmtPct(c.quote_to_sale_pct)}</td>
                <td className={`text-right font-mono font-bold ${costPerSaleColor(c.cost_per_sale, costRange.min, costRange.max)}`} data-testid={`cps-${c.campaign_name}`}>{fmtMoney(c.cost_per_sale)}</td>
                <td className="text-right font-mono">{fmtMul(c.roas)}</td>
              </tr>
            ))}
            <tr className="border-t-2 bg-muted/40 font-semibold">
              <td className="py-1.5 pr-2" colSpan={4}>TOTAL</td>
              <td className="text-right font-mono">{fmtMoney(data.totals.spend)}</td>
              <td className="text-right font-mono">{fmtInt(data.totals.clicks)}</td>
              <td className="text-right font-mono">{fmtMoney2(data.totals.cpc)}</td>
              <td className="text-right font-mono">{fmtInt(data.totals.conversions)}</td>
              <td className="text-right font-mono">{fmtMoney2(data.totals.cost_per_conversion)}</td>
              <td className="text-right font-mono">{fmtInt(data.totals.quotes_booked)}</td>
              <td className="text-right font-mono">{fmtPct(data.totals.conv_to_quote_pct)}</td>
              <td className="text-right font-mono">{fmtMoney2(data.totals.cost_per_quote)}</td>
              <td className="text-right font-mono">{fmtInt(data.totals.sales_won)}</td>
              <td className="text-right font-mono">{fmtPct(data.totals.quote_to_sale_pct)}</td>
              <td className="text-right font-mono font-bold">{fmtMoney(data.totals.cost_per_sale)}</td>
              <td className="text-right font-mono">{fmtMul(data.totals.roas)}</td>
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function SortableTh({
  label, k, sortKey, sortDir, onSort, right, hot,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  onSort: (k: SortKey) => void;
  right?: boolean;
  hot?: boolean;
}) {
  const active = sortKey === k;
  return (
    <th
      className={`${right ? "text-right" : ""} py-1 cursor-pointer select-none whitespace-nowrap ${active ? "text-foreground" : ""} ${hot ? "text-foreground font-semibold" : ""}`}
      onClick={() => onSort(k)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}

function Section2Handlers({ data }: { data: MIData }) {
  const handlers = data.handlers.map((h) => h.handler_name);
  const campaigns = useMemo(() => {
    const set = new Set<string>();
    data.handlerCampaignMatrix.forEach((r) => { if (r.campaign_name) set.add(r.campaign_name); });
    return Array.from(set);
  }, [data.handlerCampaignMatrix]);
  const byKey = new Map<string, HandlerCampaignCell>();
  data.handlerCampaignMatrix.forEach((r) => {
    if (r.campaign_name) byKey.set(`${r.handler_name}::${r.campaign_name}`, r);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">2. Who books the appointments</CardTitle>
        <p className="text-xs text-muted-foreground">Route each lead source to whoever converts it best.</p>
      </CardHeader>
      <CardContent className="overflow-x-auto space-y-5">
        <div>
          <div className="text-sm font-medium mb-2">Handler league</div>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="py-1">Handler</th>
                <th className="text-right">Leads</th>
                <th className="text-right">Appointments</th>
                <th className="text-right">Opps won</th>
                <th className="text-right">Win %</th>
              </tr>
            </thead>
            <tbody>
              {data.handlers.map((h) => (
                <tr key={h.handler_name} className="border-t">
                  <td className="py-1.5 pr-2 font-medium">{h.handler_name}</td>
                  <td className="text-right font-mono">{fmtInt(h.leads_handled)}</td>
                  <td className="text-right font-mono">{fmtInt(h.appointments_booked)}</td>
                  <td className="text-right font-mono">{fmtInt(h.opps_won ?? null)}</td>
                  <td className="text-right font-mono">
                    {fmtPct(h.win_pct ?? h.lead_to_appt_pct)}
                    {h.small_n && <SmallNTag />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <div className="text-sm font-medium mb-2">Handler × Product — Win % on opportunities they created</div>
          <table className="text-sm border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-background text-left py-1 pr-2 font-medium border-b">Handler</th>
                {campaigns.map((c) => (
                  <th key={c} className="px-2 py-1 text-xs whitespace-nowrap border-b">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {handlers.map((h) => (
                <tr key={h} className="border-t">
                  <td className="sticky left-0 bg-background py-1.5 pr-2 font-medium">{h}</td>
                  {campaigns.map((c) => {
                    const cell = byKey.get(`${h}::${c}`);
                    if (!cell) return <td key={c} className="px-2 py-1 text-center text-muted-foreground">—</td>;
                    return (
                      <td key={c} className="px-2 py-1 text-center font-mono" data-testid={`hmx-${h}-${c}`}>
                        {fmtPct(cell.win_pct ?? cell.lead_to_appt_pct)}
                        {cell.small_n && <SmallNTag />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Section3Salespeople({ data }: { data: MIData }) {
  const people = data.salespeople.map((s) => s.salesperson_name);
  const products = useMemo(() => {
    const s = new Set<string>();
    data.salespersonProductConversion.forEach((r) => s.add(r.product));
    return Array.from(s);
  }, [data.salespersonProductConversion]);
  const convKey = new Map(data.salespersonProductConversion.map((r) => [`${r.salesperson_name}::${r.product}`, r] as const));
  const qKey = new Map(data.salespersonProductQuotes.map((r) => [`${r.salesperson_name}::${r.product}`, r] as const));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">3. Who sells what</CardTitle>
        <p className="text-xs text-muted-foreground">Route each product's quotes to its strongest closer.</p>
      </CardHeader>
      <CardContent className="overflow-x-auto space-y-5">
        <div>
          <div className="text-sm font-medium mb-2">Salesperson league</div>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="py-1">Salesperson</th>
                <th className="text-right">Quotes</th>
                <th className="text-right">Sales</th>
                <th className="text-right">Conversion %</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Avg job value</th>
              </tr>
            </thead>
            <tbody>
              {data.salespeople.map((p) => (
                <tr key={p.salesperson_name} className="border-t">
                  <td className="py-1.5 pr-2 font-medium">{p.salesperson_name}</td>
                  <td className="text-right font-mono">{fmtInt(p.quotes_issued)}</td>
                  <td className="text-right font-mono">{fmtInt(p.sales_won)}</td>
                  <td className="text-right font-mono">
                    {fmtPct(p.conversion_pct)}
                    {p.small_n && <SmallNTag />}
                  </td>
                  <td className="text-right font-mono">{fmtMoney(p.revenue_won)}</td>
                  <td className="text-right font-mono">{fmtMoney(p.avg_job_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <div className="text-sm font-medium mb-2">Salesperson × Product — Quote → Sale conversion %</div>
          <ProductMatrix
            people={people} products={products}
            cell={(p, prod) => {
              const c = convKey.get(`${p}::${prod}`);
              if (!c) return null;
              return (
                <span className="font-mono" data-testid={`spx-conv-${p}-${prod}`}>
                  {fmtPct(c.conversion_pct ?? null)}
                  {c.small_n && <SmallNTag />}
                </span>
              );
            }}
          />
        </div>

        <div>
          <div className="text-sm font-medium mb-2">Salesperson × Product — Quotes issued (volume)</div>
          <ProductMatrix
            people={people} products={products}
            cell={(p, prod) => {
              const c = qKey.get(`${p}::${prod}`);
              if (!c) return null;
              return <span className="font-mono">{fmtInt(c.quotes_issued)}</span>;
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ProductMatrix({
  people, products, cell,
}: {
  people: string[];
  products: string[];
  cell: (p: string, product: string) => React.ReactNode;
}) {
  return (
    <table className="text-sm border-collapse">
      <thead>
        <tr>
          <th className="sticky left-0 bg-background text-left py-1 pr-2 font-medium border-b">Salesperson</th>
          {products.map((p) => (
            <th key={p} className="px-2 py-1 text-xs whitespace-nowrap border-b">{p}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {people.map((p) => (
          <tr key={p} className="border-t">
            <td className="sticky left-0 bg-background py-1.5 pr-2 font-medium">{p}</td>
            {products.map((prod) => (
              <td key={prod} className="px-2 py-1 text-center">
                {cell(p, prod) ?? <span className="text-muted-foreground">—</span>}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SmallNTag() {
  return (
    <span title="Low volume — indicative only (denominator under 10)" className="ml-1 text-amber-600 dark:text-amber-400 text-xs">
      <AlertCircle className="h-3 w-3 inline" />
    </span>
  );
}
